import { useEffect } from 'react';
import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';

// Login type options
const loginTypeOptions = [
  { value: '', text: '--Choose Login Type--' },
  { value: 'userid', text: 'User ID/Password' },
  { value: 'email', text: 'Google OAuth' },
  { value: 'sso', text: 'SSO' },
];

// Default status options (fallback)
const defaultStatusOptions = [
  { value: 'Active', text: 'Active' },
  { value: 'Inactive', text: 'Inactive' },
];

const UsersModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
  roleOptions,
  statusOptions = defaultStatusOptions,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setFocus,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      role: '',
      loginType: '',
      userStatus: 'Active',
    },
  });

  // Reset form when modal opens or defaultValues change
  useEffect(() => {
    if (isOpen && defaultValues) {
      reset(defaultValues);
    }
  }, [isOpen, defaultValues, reset]);

  const handleClose = () => {
    reset({
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      role: '',
      loginType: '',
      userStatus: 'Active',
    });
    onClose();
  };

  const handleFormSubmit = async (formData) => {
    const result = await onSubmit(formData);
    // If the parent returns field errors, show them inline
    if (result?.fieldErrors) {
      Object.entries(result.fieldErrors).forEach(([field, message]) => {
        setError(field, { type: 'server', message });
      });
      // Focus the first errored field
      const firstField = Object.keys(result.fieldErrors)[0];
      if (firstField) {
        try { setFocus(firstField); } catch (_) { /* field may not be focusable */ }
      }
    }
  };

  // Helper: input border class based on error state
  const inputClass = (fieldName, extra = '') =>
    `outline-none border-2 rounded-lg px-3 py-2 ${
      errors[fieldName] ? 'border-red-500 text-red-700' : 'border-secondary-500'
    } ${extra}`;

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[900px] max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Modal Title and Close Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">
              {isEditMode ? 'Edit User' : 'Add User'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form
            className="flex flex-col gap-4 md:gap-6"
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            {/* Row 1: First Name, Last Name, User ID */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
              <div className="flex flex-col w-full space-y-1">
                <label htmlFor="firstName" className="text-text-hint mb-1">
                  First Name <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('firstName', {
                    required: 'First Name is required',
                    minLength: { value: 2, message: 'First Name must be at least 2 characters' },
                  })}
                  type="text"
                  placeholder="Enter first name"
                  id="firstName"
                  className={inputClass('firstName')}
                />
                {errors.firstName && (
                  <span className="text-red-500 text-xs">{errors.firstName.message}</span>
                )}
              </div>

              <div className="flex flex-col w-full space-y-1">
                <label htmlFor="lastName" className="text-text-hint mb-1">
                  Last Name <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('lastName', {
                    required: 'Last Name is required',
                    minLength: { value: 2, message: 'Last Name must be at least 2 characters' },
                  })}
                  type="text"
                  placeholder="Enter last name"
                  id="lastName"
                  className={inputClass('lastName')}
                />
                {errors.lastName && (
                  <span className="text-red-500 text-xs">{errors.lastName.message}</span>
                )}
              </div>

              <div className="flex flex-col w-full space-y-1">
                <label htmlFor="userName" className="text-text-hint mb-1">
                  User ID <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('userName', {
                    required: 'User ID is required',
                    minLength: { value: 3, message: 'User ID must be at least 3 characters' },
                  })}
                  type="text"
                  placeholder="Enter user ID"
                  id="userName"
                  disabled={isEditMode}
                  className={inputClass('userName', isEditMode ? 'bg-gray-100 cursor-not-allowed text-gray-500' : '')}
                />
                {errors.userName && (
                  <span className="text-red-500 text-xs">{errors.userName.message}</span>
                )}
              </div>
            </div>

            {/* Row 2: Email and Role */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-8">
              <div className="flex flex-col w-full space-y-1">
                <label htmlFor="email" className="text-text-hint mb-1">
                  Email ID <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address',
                    },
                  })}
                  type="email"
                  placeholder="youremail@gmail.com"
                  id="email"
                  className={inputClass('email')}
                />
                {errors.email && (
                  <span className="text-red-500 text-xs">{errors.email.message}</span>
                )}
              </div>

              <div className="flex flex-col w-full space-y-1">
                <label htmlFor="role" className="text-text-hint mb-1">
                  Role <span className="text-danger-500">*</span>
                </label>
                <select
                  {...register('role', { required: 'Role is required' })}
                  id="role"
                  className={inputClass('role')}
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <span className="text-red-500 text-xs">{errors.role.message}</span>
                )}
              </div>
            </div>

            {/* Row 3: Login Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-8">
              <div className="flex flex-col w-full space-y-1">
                <label htmlFor="loginType" className="text-text-hint mb-1">
                  Login Type <span className="text-danger-500">*</span>
                </label>
                <select
                  {...register('loginType', { required: 'Login Type is required' })}
                  id="loginType"
                  className={inputClass('loginType')}
                >
                  {loginTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
                {errors.loginType && (
                  <span className="text-red-500 text-xs">{errors.loginType.message}</span>
                )}
              </div>
            </div>

            {/* Row 4: User Status (radio buttons) */}
            <div className="flex flex-col w-full space-y-1">
              <label className="text-text-hint mb-1">
                User Status <span className="text-danger-500">*</span>
              </label>
              <ul className="flex flex-wrap mt-1">
                {statusOptions.map((option, index) => (
                  <li key={option.value} className="relative">
                    <input
                      {...register('userStatus', { required: 'User Status is required' })}
                      className="sr-only peer"
                      type="radio"
                      value={option.value}
                      id={`status-${option.value}`}
                    />
                    <label
                      className={`flex justify-center items-center py-2 px-4 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800 ${
                        index === 0
                          ? 'rounded-tl-full rounded-bl-full'
                          : index === statusOptions.length - 1
                          ? 'rounded-tr-full rounded-br-full'
                          : ''
                      }`}
                      htmlFor={`status-${option.value}`}
                    >
                      {option.text}
                    </label>
                  </li>
                ))}
              </ul>
              {errors.userStatus && (
                <span className="text-red-500 text-xs">{errors.userStatus.message}</span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-secondary-700 text-text-light py-2 px-6 rounded-lg shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Saving...'
                    : isEditMode
                    ? 'Update'
                    : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default UsersModal;
